/**
* Web.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { getCookie, objDefineAccessors, getWindow, getLocation } from "@ms/1ds-core-js";
var BROWSERS = {
    MSIE: "MSIE",
    CHROME: "Chrome",
    FIREFOX: "Firefox",
    SAFARI: "Safari",
    EDGE: "Edge",
    ELECTRON: "Electron",
    SKYPE_SHELL: "SkypeShell",
    PHANTOMJS: "PhantomJS",
    OPERA: "Opera"
};
var REGEX_VERSION = "([\\d,.]+)";
var UNKNOWN = "Unknown";
var Web = /** @class */ (function () {
    function Web(propertiesConfig) {
        this._propertiesConfig = propertiesConfig;
        // Add the domain
        var windowLocation = getLocation();
        if (windowLocation) {
            var domain = windowLocation.hostname;
            if (domain) {
                this.domain = windowLocation.protocol === "file:" ? "local" : domain;
            }
        }
        var userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
        if (propertiesConfig.userAgent) {
            userAgent = propertiesConfig.userAgent;
        }
        if (propertiesConfig.populateBrowserInfo) {
            if (userAgent) {
                var browserName = this._getBrowserName(userAgent);
                this.browser = browserName;
                this.browserVer = this._getBrowserVersion(userAgent, browserName);
            }
            var screenRes = this._getScreenResolution();
            this.screenRes = screenRes.w + "X" + screenRes.h;
        }
    }
    /**
    * Describes whether the user has given consent for Cookies.
    * This is configurable by users to define which cookie to look for marking this flag as true or false.
    */
    Web.prototype.getUserConsent = function () {
        return this._propertiesConfig.userConsented || (getCookie(this._propertiesConfig.userConsentCookieName || "MSCC") ? true : false);
    };
    /**
    *
    ** Function to retrieve user consent details.
    * @param callback - Callback function to get user consent details
    * @returns IUserContentDetails stringified object
    */
    Web.prototype.getUserConsentDetails = function () {
        try {
            if (this._propertiesConfig.callback && this._propertiesConfig.callback.userConsentDetails) {
                var result = this._propertiesConfig.callback.userConsentDetails();
                if (result) {
                    return JSON.stringify({
                        Required: result.Required ? result.Required : false,
                        Analytics: result.Analytics ? result.Analytics : false,
                        SocialMedia: result.SocialMedia ? result.SocialMedia : false,
                        Advertising: result.Advertising ? result.Advertising : false
                    });
                }
            }
        }
        catch (e) {
            //
        }
        return null;
    };
    Web.prototype._getBrowserName = function (userAgent) {
        // Check for Opera first
        if (this._userAgentContainsString("OPR/", userAgent)) {
            return BROWSERS.OPERA;
        }
        // Check for Phantom JS
        if (this._userAgentContainsString(BROWSERS.PHANTOMJS, userAgent)) {
            return BROWSERS.PHANTOMJS;
        }
        // Check for Edge
        if (this._userAgentContainsString(BROWSERS.EDGE, userAgent)) {
            return BROWSERS.EDGE;
        }
        // Check for Electron
        if (this._userAgentContainsString(BROWSERS.ELECTRON, userAgent)) {
            return BROWSERS.ELECTRON;
        }
        // Check for Chrome
        if (this._userAgentContainsString(BROWSERS.CHROME, userAgent)) {
            return BROWSERS.CHROME;
        }
        // Check for Internet Explorer
        if (this._userAgentContainsString("Trident", userAgent)) {
            return BROWSERS.MSIE;
        }
        // Check for Firefox
        if (this._userAgentContainsString(BROWSERS.FIREFOX, userAgent)) {
            return BROWSERS.FIREFOX;
        }
        // Check for Safari
        if (this._userAgentContainsString(BROWSERS.SAFARI, userAgent)) {
            return BROWSERS.SAFARI;
        }
        // Check for Skype shell
        if (this._userAgentContainsString(BROWSERS.SKYPE_SHELL, userAgent)) {
            return BROWSERS.SKYPE_SHELL;
        }
        return UNKNOWN;
    };
    Web.prototype._userAgentContainsString = function (searchString, userAgent) {
        return userAgent.indexOf(searchString) > -1;
    };
    Web.prototype._getBrowserVersion = function (userAgent, browserName) {
        if (browserName === BROWSERS.MSIE) {
            return this._getIeVersion(userAgent);
        }
        else {
            return this._getOtherVersion(browserName, userAgent);
        }
    };
    Web.prototype._getIeVersion = function (userAgent) {
        var classicIeVersionMatches = userAgent.match(new RegExp(BROWSERS.MSIE + " " + REGEX_VERSION));
        if (classicIeVersionMatches) {
            return classicIeVersionMatches[1];
        }
        else {
            var ieVersionMatches = userAgent.match(new RegExp("rv:" + REGEX_VERSION));
            if (ieVersionMatches) {
                return ieVersionMatches[1];
            }
        }
    };
    Web.prototype._getOtherVersion = function (browserString, userAgent) {
        if (browserString === BROWSERS.SAFARI) {
            browserString = "Version";
        }
        var matches = userAgent.match(new RegExp(browserString + "/" + REGEX_VERSION));
        if (matches) {
            return matches[1];
        }
        return UNKNOWN;
    };
    /**
    * Get Screen resolution
    * @returns {ScreenResolution} - Screen resolution
    */
    Web.prototype._getScreenResolution = function () {
        var screenRes = { h: 0, w: 0 };
        var win = getWindow();
        if (win && win.screen) {
            screenRes.h = screen.height;
            screenRes.w = screen.width;
        }
        return screenRes;
    };
    /**
     * Static constructor, attempt to create accessors
     */
    Web._staticInit = (function () {
        // Dynamically create get/set property accessors
        objDefineAccessors(Web.prototype, "userConsent", Web.prototype.getUserConsent);
    })();
    return Web;
}());
export { Web };
//# sourceMappingURL=Web.js.map